// Dates, times and small text helpers. All dates shown in India time.
const TZ = 'Asia/Kolkata';

function parseDate(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 6, 30)); // midday in India, so the date never shifts
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Pieces of a date in India time, so the order and commas are ours ("Monday, 8 December 2025").
function parts(d, options) {
  const out = {};
  for (const p of new Intl.DateTimeFormat('en-IN', { ...options, timeZone: TZ }).formatToParts(d)) out[p.type] = p.value;
  return out;
}

export function dateLong(value) {
  const d = parseDate(value);
  if (!d) return '';
  const p = parts(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `${p.weekday}, ${p.day} ${p.month} ${p.year}`;
}

export function dateShort(value) {
  const d = parseDate(value);
  if (!d) return '';
  const p = parts(d, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${p.day} ${p.month} ${p.year}`;
}

export function dateTile(value) {
  const d = parseDate(value);
  if (!d) return null;
  return {
    day: d.toLocaleDateString('en-IN', { day: 'numeric', timeZone: TZ }),
    month: d.toLocaleDateString('en-IN', { month: 'short', timeZone: TZ }),
    year: d.toLocaleDateString('en-IN', { year: '2-digit', timeZone: TZ }),
  };
}

export function monthYear(value) {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: TZ }) : '';
}

export function dateTime(value) {
  const d = parseDate(value);
  return d ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: TZ }) : '';
}

export function todayIso() {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ }); // YYYY-MM-DD
}

export function addMonths(iso, months) {
  const [y, m, d] = iso.split('-').map(Number);
  const end = new Date(Date.UTC(y, m - 1 + months, d)); // the same date later on (29 Feb rolls on to 1 Mar)
  end.setUTCDate(end.getUTCDate() - 1); // access runs to the day before: 12 months from 5 Oct ends on 4 Oct
  return end.toISOString().slice(0, 10);
}

export function daysUntil(iso) {
  if (!iso) return null;
  return Math.round((Date.parse(iso) - Date.parse(todayIso())) / 86400000);
}

export function duration(ms) {
  if (!ms) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export function greeting() {
  const hour = Number(new Date().toLocaleString('en-IN', { hour: 'numeric', hour12: false, timeZone: TZ }));
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// A title at the start of a name, as some academy accounts have in Microsoft ("Miss. Isha Prayag"). It
// counts only when a full stop or a space follows, so "Mrinal" or "Drashti" stay as they are.
const TITLE = /^(?:miss|ms|mrs|mr|mx|dr|prof|smt|kum|shri|ca)(?:\.\s*|\s+)/i;

// The name without a title in front, for greetings and initials. A name that is only a title stays.
export function withoutTitle(name) {
  const text = String(name || '').trim();
  return text.replace(TITLE, '').trim() || text;
}

export function firstName(person) {
  const name = withoutTitle(person && person.full_name);
  if (name) return name.split(/\s+/)[0];
  return person && person.email ? person.email.split('@')[0] : '';
}

// The letter shown in a round picture: the name's first letter, after any title.
export function initialOf(name) {
  return (withoutTitle(name) || '?').charAt(0).toUpperCase();
}

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : (many || one + 's')}`;
}

// Links to recordings, notes and folders must be the academy's OneDrive/SharePoint ones (the database
// already drops anything else; this is a second check before anything becomes a link or a player).
export function msUrl(url) {
  return typeof url === 'string' && /^https:\/\/[a-z0-9-]+\.sharepoint\.com\//i.test(url) ? url : null;
}

/* ---------- times of day (timetable) ---------- */

// "7:00 pm"
export function timeLabel(value) {
  const d = parseDate(value);
  if (!d) return '';
  const p = parts(d, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${p.hour}:${p.minute} ${(p.dayPeriod || '').toLowerCase()}`.trim();
}

// "7:00–9:00 pm", or "11:30 am–1:30 pm" when it crosses noon
export function timeRange(start, end) {
  const a = timeLabel(start);
  const b = timeLabel(end);
  const pa = a.split(' ')[1];
  const pb = b.split(' ')[1];
  return pa === pb ? `${a.split(' ')[0]}–${b}` : `${a}–${b}`;
}

// The India date (YYYY-MM-DD) a moment falls on.
export function istDate(value) {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-CA', { timeZone: TZ }) : '';
}

export function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// 0 = Sunday ... 6 = Saturday, for a YYYY-MM-DD date
export function weekday(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// "Today", "Tomorrow", "Yesterday" or "", and "Monday, 5 October" (with the year if it isn't this year)
export function dayLabel(iso) {
  const today = todayIso();
  const prefix = iso === today ? 'Today' : iso === addDays(today, 1) ? 'Tomorrow' : iso === addDays(today, -1) ? 'Yesterday' : '';
  const p = parts(parseDate(iso), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const year = iso.slice(0, 4) === today.slice(0, 4) ? '' : ` ${p.year}`;
  return { prefix, text: `${p.weekday}, ${p.day} ${p.month}${year}` };
}

// "2 hr", "1 hr 30 min", "45 min"
export function minutesLabel(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

// Teams meeting links only; anything else is not shown as a link.
export function teamsUrl(url) {
  return typeof url === 'string' && /^https:\/\/(teams\.microsoft\.com|teams\.cloud\.microsoft|teams\.live\.com)\//i.test(url) ? url : null;
}

// "19:00": the India time of day of a moment, for time inputs
export function istTime(value) {
  const d = parseDate(value);
  if (!d) return '';
  const p = parts(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${p.hour}:${p.minute}`;
}

// Every date from `from` to `until` (inclusive) that falls on one of `days` (0 = Sunday), at most 150.
export function weeklyDates(from, until, days) {
  const out = [];
  if (!from || !until || !days.length || until < from) return out;
  for (let d = from, i = 0; d <= until && i < 400 && out.length < 150; d = addDays(d, 1), i++) {
    if (days.includes(weekday(d))) out.push(d);
  }
  return out;
}

/* ---------- money ---------- */

// 7000000 paise -> "₹70,000", "₹1,30,000.50" (Indian grouping)
export function rupees(paise) {
  const value = Number(paise || 0) / 100;
  const whole = Number.isInteger(value);
  return '₹' + value.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function belowHundred(n) {
  return n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? `-${ONES[n % 10]}` : ''}`;
}
function belowThousand(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  return [h ? `${ONES[h]} hundred` : '', r ? belowHundred(r) : ''].filter(Boolean).join(' and ');
}

// 13000000 paise -> "Rupees one lakh thirty thousand only" (Indian system: thousand, lakh, crore)
export function rupeesInWords(paise) {
  const total = Math.round(Number(paise || 0));
  let n = Math.floor(total / 100);
  const p = total % 100;
  if (!n && !p) return 'Rupees zero only';
  const parts = [];
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) parts.push(`${belowThousand(crore)} crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} thousand`);
  if (n) parts.push(belowThousand(n));
  const words = parts.join(' ');
  const paiseWords = p ? ` and ${belowHundred(p)} paise` : '';
  return `Rupees ${words || 'zero'}${paiseWords} only`;
}

export const METHODS = { upi: 'UPI', card: 'Card', netbanking: 'Net banking', emi: 'EMI', cardless_emi: 'Cardless EMI',
  wallet: 'Wallet', paylater: 'Pay later', neft: 'Bank transfer (NEFT/IMPS)', cash: 'Cash', cheque: 'Cheque', other: 'Other' };
export const methodLabel = (m) => METHODS[m] || (m ? m[0].toUpperCase() + m.slice(1) : '');
